
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { useContacts } from '@/hooks/useContacts';
import { Contract, ContactPerson } from '@/lib/types';
import { contractPdfGenerator } from '@/services/ContractPdfGenerator';
import { useSupabaseClient } from '@/hooks/useSupabaseClient';
import { useToast } from '@/hooks/use-toast';
import { Search, Mail, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface SendContractEmailDialogProps {
  contract: Contract;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SendContractEmailDialog({ contract, open, onOpenChange }: SendContractEmailDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const supabase = useSupabaseClient();
  const [recipient, setRecipient] = useState('');
  const [subject, setSubject] = useState(`Contract: ${contract.supplierName}`);
  const [message, setMessage] = useState('');
  const [includeAdditionalAttachments, setIncludeAdditionalAttachments] = useState(false);
  const [showContactSearch, setShowContactSearch] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  const { useContactsQuery } = useContacts();
  const { data: contacts = [] } = useContactsQuery();

  const handleContactSelect = (contact: ContactPerson) => {
    setRecipient(contact.email);
    setShowContactSearch(false);
  };

  const handleSend = async () => {
    if (!recipient || !subject || !message) {
      toast({
        title: t('error'),
        description: t('fillAllFields'),
        variant: 'destructive',
      });
      return;
    }

    setIsSending(true);
    setIsPdfGenerating(true);
    
    try {
      // Generate contract PDF
      const pdfBlob = contractPdfGenerator.generateContractPdf(contract);
      const arrayBuffer = await pdfBlob.arrayBuffer();
      const base64Data = btoa(
        new Uint8Array(arrayBuffer).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          ''
        )
      );
      
      setIsPdfGenerating(false);
      console.log("Contract PDF generated, size:", arrayBuffer.byteLength, "bytes");

      // Prepare attachments data
      const attachmentsData = contract.attachments.map(attachment => ({
        name: attachment.name,
        type: attachment.type,
        url: attachment.url.split('/contract_attachments/')[1] // Get the relative path
      })).filter(att => att.url); // Filter out any invalid URLs

      console.log("Prepared attachments:", attachmentsData);

      // Call the edge function to send email
      const { data, error } = await supabase.functions.invoke('send-contract-email', {
        body: {
          contractId: contract.id,
          recipient,
          subject,
          message,
          includeAdditionalAttachments,
          pdfContent: base64Data,
          filename: `contract-${contract.id}.pdf`,
          additionalAttachments: includeAdditionalAttachments ? attachmentsData : []
        },
      });

      if (error) throw error;
      
      if (data?.error) {
        console.error("Edge function returned error:", data.error);
        throw new Error(data.error.message || "Failed to send email");
      }

      toast({
        title: t('success'),
        description: t('emailSent'),
      });

      onOpenChange(false);
    } catch (error) {
      console.error('Error sending contract email:', error);
      toast({
        title: t('error'),
        description: t('errorSendingEmail'),
        variant: 'destructive',
      });
    } finally {
      setIsSending(false);
      setIsPdfGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('sendContract')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>{t('recipient')}</Label>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder={t('emailAddress')}
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setShowContactSearch(!showContactSearch)}
              >
                <Search className="h-4 w-4" />
              </Button>
            </div>
            {showContactSearch && contacts.length > 0 && (
              <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-background shadow-lg">
                {contacts.map((contact) => (
                  <button
                    key={contact.id}
                    className="w-full px-4 py-2 text-left hover:bg-accent"
                    onClick={() => handleContactSelect(contact)}
                  >
                    <div className="font-medium">{contact.name}</div>
                    <div className="text-sm text-muted-foreground">{contact.email}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>{t('subject')}</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>{t('message')}</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
            />
          </div>

          {contract.attachments.length > 0 && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id="attachments"
                checked={includeAdditionalAttachments}
                onCheckedChange={(checked) => setIncludeAdditionalAttachments(checked as boolean)}
              />
              <Label htmlFor="attachments">
                {t('includeAdditionalAttachments')} ({contract.attachments.length})
              </Label>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleSend} 
              disabled={isSending || isPdfGenerating}
              className="min-w-[100px]"
            >
              {(isSending || isPdfGenerating) ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {isPdfGenerating ? t('generatingPdf') : t('sending')}
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  {t('send')}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
